import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import {
    Upload,
    Loader2,
    Users,
    Files,
    CheckCircle2,
    AlertTriangle,
    FileJson,
    Server,
    Database,
    Eye,
    Info,
    FileDown,
    FileSpreadsheet,
    BookUser,
} from "lucide-react";
import { toast } from "sonner";
import { formatBytes } from "../lib/documents";
import { buildMigrationReportPdf } from "../lib/migrationReport";

export const MigrationDialog = ({ open, onOpenChange, onImported }) => {
    const [status, setStatus] = useState(null);
    const [tab, setTab] = useState("sql");

    const fetchStatus = async () => {
        try {
            const { data } = await api.get("/migration/status");
            setStatus(data);
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        if (open) fetchStatus();
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="migration-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Server className="h-5 w-5 text-[#376DB3]" />
                        Migrazione vecchio portale
                    </DialogTitle>
                    <DialogDescription>
                        Importa utenti e documenti dal vecchio portale Splendor Sud (dip.splendorsud.com).
                        Il modo più semplice è caricare il <b>dump SQL</b> esportato da phpMyAdmin.
                    </DialogDescription>
                </DialogHeader>

                {status && (
                    <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-xs sm:grid-cols-4">
                        <Stat label="Dipendenti" value={status.total_employees} />
                        <Stat label="Migrati" value={status.migrated_employees} highlight />
                        <Stat label="Con password" value={status.employees_with_password} />
                        <Stat label="Documenti" value={status.total_documents} />
                    </div>
                )}

                <Tabs value={tab} onValueChange={setTab} className="mt-2">
                    <TabsList className="w-full">
                        <TabsTrigger value="sql" className="flex-1 gap-2" data-testid="migration-tab-sql">
                            <Database className="h-4 w-4" /> 1. SQL
                        </TabsTrigger>
                        <TabsTrigger value="libro" className="flex-1 gap-2" data-testid="migration-tab-libro">
                            <BookUser className="h-4 w-4" /> 2. Anagrafica
                        </TabsTrigger>
                        <TabsTrigger value="files" className="flex-1 gap-2" data-testid="migration-tab-files">
                            <Files className="h-4 w-4" /> 3. Documenti
                        </TabsTrigger>
                        <TabsTrigger value="users" className="flex-1 gap-2" data-testid="migration-tab-users">
                            <Users className="h-4 w-4" /> JSON
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="sql" className="mt-4">
                        <SqlImportPanel onImported={() => { fetchStatus(); onImported && onImported(); }} />
                    </TabsContent>
                    <TabsContent value="libro" className="mt-4">
                        <LibroMatricolaPanel onImported={() => { fetchStatus(); onImported && onImported(); }} />
                    </TabsContent>
                    <TabsContent value="files" className="mt-4">
                        <FilesImportPanel onImported={() => { fetchStatus(); onImported && onImported(); }} />
                    </TabsContent>
                    <TabsContent value="users" className="mt-4">
                        <UsersImportPanel onImported={() => { fetchStatus(); onImported && onImported(); }} />
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="migration-close">Chiudi</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Stat = ({ label, value, highlight }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`font-mono-numbers font-display text-xl font-bold ${highlight ? "text-[#376DB3]" : "text-slate-700"}`}>
            {value ?? 0}
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// SQL panel — upload phpMyAdmin export, preview, then import
// ---------------------------------------------------------------------------
const SqlImportPanel = ({ onImported }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const ref = useRef(null);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setResult(null);
        if (ref.current) ref.current.value = "";
    };

    const doPreview = async () => {
        if (!file) return;
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("sql", file);
            const { data } = await api.post("/migration/sql-preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setPreview(data);
            if (!data.detected_table) {
                toast.warning("Nessuna tabella utenti rilevata nel file SQL.");
            } else {
                toast.success(`Rilevati ${data.row_count} record nella tabella "${data.detected_table}".`);
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore nell'analisi del dump SQL");
        } finally {
            setBusy(false);
        }
    };

    const doImport = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("sql", file);
            const { data } = await api.post("/migration/sql-import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000,
            });
            setResult(data);
            toast.success(`${data.created} creati, ${data.updated} aggiornati`);
            onImported && onImported();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore nell'import SQL");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900">
                <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#376DB3]" />
                    <div>
                        <b>Come scaricare il dump SQL da phpMyAdmin:</b>
                        <ol className="ml-4 mt-1 list-decimal space-y-0.5">
                            <li>Apri phpMyAdmin del tuo vecchio portale (dal pannello di controllo Register.it).</li>
                            <li>Seleziona il database del portale a sinistra.</li>
                            <li>Clicca sulla scheda <b>"Esporta"</b> in alto.</li>
                            <li>Lascia <b>"Rapido"</b> e formato <b>"SQL"</b>, poi clicca <b>"Esegui"</b>.</li>
                            <li>Carica il file scaricato qui sotto.</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
                <Database className="mx-auto h-8 w-8 text-slate-400" />
                <div className="mt-2 font-display text-sm font-semibold text-[#376DB3]">
                    {file ? file.name : "database.sql"}
                </div>
                {file && <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>}
                <input
                    ref={ref}
                    type="file"
                    accept=".sql,.txt,text/plain,application/sql"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
                    className="hidden"
                    data-testid="migration-sql-input"
                />
                <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => ref.current?.click()}>
                    <Upload className="h-4 w-4" />
                    {file ? "Cambia file" : "Seleziona file .sql"}
                </Button>
            </div>

            <div
                className={`rounded-md border border-slate-200 bg-white p-3 ${preview && preview.detected_table ? "" : "hidden"}`}
                data-testid="migration-sql-preview"
            >
                <div className="flex items-center gap-2 text-sm">
                    <Eye className="h-4 w-4 text-[#376DB3]" />
                    <b>Anteprima:</b>
                    <span>tabella <code className="rounded bg-slate-100 px-1">{preview?.detected_table || ""}</code> · <b>{preview?.row_count || 0}</b> record</span>
                </div>
                <div className={`mt-2 max-h-44 overflow-y-auto rounded border border-slate-100 text-xs ${preview?.sample_users?.length > 0 ? "" : "hidden"}`}>
                    <table className="w-full">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="px-2 py-1 text-left">Email</th>
                                <th className="px-2 py-1 text-left">Cognome</th>
                                <th className="px-2 py-1 text-left">Nome</th>
                                <th className="px-2 py-1 text-left">Matricola</th>
                                <th className="px-2 py-1 text-left">Password</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(preview?.sample_users || []).slice(0, 10).map((u, i) => (
                                <tr key={u.email || `row-${i}`} className="border-t border-slate-100">
                                    <td className="px-2 py-1 font-mono text-[11px]">{u.email || "—"}</td>
                                    <td className="px-2 py-1">{u.cognome || "—"}</td>
                                    <td className="px-2 py-1">{u.nome || "—"}</td>
                                    <td className="px-2 py-1">{u.matricola || "—"}</td>
                                    <td className="px-2 py-1">{u.password_hash ? <span className="text-emerald-700">✓</span> : <span className="text-slate-400">—</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 ${preview && !preview.detected_table ? "" : "hidden"}`}>
                <div className="flex items-center gap-1 font-semibold">
                    <AlertTriangle className="h-3.5 w-3.5" /> Nessuna tabella utenti riconosciuta
                </div>
                <div className="mt-1">
                    {preview?.warning || ""}
                    <span className={preview?.tables_found?.length > 0 ? "" : "hidden"}>
                        {" "}Tabelle trovate: {(preview?.tables_found || []).map((t) => `${t.name} (${t.row_count} righe)`).join(", ")}.
                    </span>
                </div>
            </div>

            <div className={`space-y-2 ${result ? "" : "hidden"}`}>
                <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <div className="text-sm text-emerald-900">
                        <b>{result?.created || 0}</b> creati, <b>{result?.updated || 0}</b> aggiornati, <b>{result?.password_imported || 0}</b> con password importata
                        <span className={result?.needs_reset > 0 ? "" : "hidden"}>, <b className="text-amber-700">{result?.needs_reset || 0}</b> necessitano reset</span>
                    </div>
                </div>
                <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 ${result?.errors?.length > 0 ? "" : "hidden"}`}>
                    <div className="font-semibold">Errori ({result?.errors?.length || 0})</div>
                    <div className="mt-1 max-h-32 overflow-y-auto">
                        {(result?.errors || []).slice(0, 20).map((e, i) => (
                            <div key={`err-${e.index}-${i}`}>Riga {e.index}: {e.error}</div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    onClick={reset}
                    data-testid="migration-sql-reset"
                    className={preview || result ? "" : "hidden"}
                >
                    Nuovo file
                </Button>
                <Button
                    variant="outline"
                    onClick={() => buildMigrationReportPdf(result)}
                    className={`gap-2 ${result ? "" : "hidden"}`}
                    data-testid="migration-sql-report-pdf"
                >
                    <FileDown className="h-4 w-4" />
                    Scarica report PDF
                </Button>
                <Button
                    onClick={doPreview}
                    disabled={!file || busy}
                    variant="outline"
                    className={`gap-2 ${!preview ? "" : "hidden"}`}
                    data-testid="migration-sql-preview-btn"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Analizza file
                </Button>
                <Button
                    onClick={doImport}
                    disabled={!file || busy}
                    className={`gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90 ${preview?.detected_table && !result ? "" : "hidden"}`}
                    data-testid="migration-sql-import-btn"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Importa {preview?.row_count || 0} utenti
                </Button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// LIBRO MATRICOLA panel — upload .xlsx and update existing employees by matricola
// ---------------------------------------------------------------------------
const LibroMatricolaPanel = ({ onImported }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const ref = useRef(null);

    const reset = () => {
        setFile(null);
        setResult(null);
        if (ref.current) ref.current.value = "";
    };

    const handleSubmit = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/migration/libro-matricola", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 300000,
            });
            setResult(data);
            toast.success(`${data.updated} dipendenti aggiornati`);
            onImported && onImported();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore aggiornamento anagrafica");
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = async () => {
        // Lazy import to keep initial bundle smaller
        const { buildLibroMatricolaReportPdf } = await import("../lib/libroMatricolaReport");
        buildLibroMatricolaReportPdf(result);
    };

    return (
        <div className="space-y-3">
            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900">
                <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#376DB3]" />
                    <div>
                        <b>Come funziona:</b> carica il <b>Libro Matricola</b> in formato Excel (.xlsx).
                        Il sistema leggerà entrambi i fogli (<b>Operai</b> e <b>Cessati Operai</b>) e:
                        <ul className="ml-4 mt-1 list-disc">
                            <li><b>Aggiorna</b> i dipendenti già in portale (match per matricola o cognome+nome)</li>
                            <li><b>Crea</b> i dipendenti del foglio Operai non presenti in portale</li>
                            <li><b>Segnala</b> nel report i Cessati non presenti</li>
                        </ul>
                        <br />
                        <span className="text-amber-800">⚠ Password, foto ed email non vengono mai modificate.</span>
                    </div>
                </div>
            </div>

            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
                <FileSpreadsheet className="mx-auto h-8 w-8 text-slate-400" />
                <div className="mt-2 font-display text-sm font-semibold text-[#376DB3]">
                    {file ? file.name : "Libro Matricola.xlsx"}
                </div>
                {file && <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>}
                <input
                    ref={ref}
                    type="file"
                    accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                    className="hidden"
                    data-testid="migration-libro-input"
                />
                <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => ref.current?.click()}>
                    <Upload className="h-4 w-4" />
                    {file ? "Cambia file" : "Seleziona file .xlsx"}
                </Button>
            </div>

            <div className={`space-y-2 ${result ? "" : "hidden"}`} data-testid="migration-libro-result">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-emerald-900">
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            <b>{result?.updated || 0}</b> aggiornati
                        </span>
                        <span className={`text-emerald-800 ${(result?.created || 0) > 0 ? "" : "hidden"}`}>
                            · <b>{result?.created || 0}</b> creati (Operai)
                        </span>
                        <span className={`text-amber-800 ${(result?.not_found || 0) > 0 ? "" : "hidden"}`}>
                            · <b>{result?.not_found || 0}</b> non in portale
                        </span>
                    </div>
                    <div className={`mt-2 text-xs text-slate-600 ${Array.isArray(result?.sheets_parsed) && result.sheets_parsed.length > 0 ? "" : "hidden"}`}>
                        Fogli letti: {(result?.sheets_parsed || []).map((s) => `${s.name} (${s.with_matricola} record)`).join(" · ")}
                    </div>
                </div>

                <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 ${(result?.not_found || 0) > 0 ? "" : "hidden"}`}>
                    <div className="flex items-center gap-1 font-semibold">
                        <AlertTriangle className="h-3 w-3" /> {result?.not_found || 0} matricole non trovate in portale
                    </div>
                    <div className="mt-1 text-amber-800">
                        Probabili motivi: dipendenti già cessati prima dell'attivazione del portale, oppure
                        manca un import precedente. Esempi:{" "}
                        <code className="rounded bg-white px-1">
                            {result?.not_found_matricole?.slice(0, 20).join(", ") || ""}
                        </code>
                    </div>
                </div>

                <div className={`rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900 ${result?.errors?.length > 0 ? "" : "hidden"}`}>
                    <div className="font-semibold">Errori ({result?.errors?.length || 0})</div>
                    <div className="mt-1 max-h-32 overflow-y-auto">
                        {(result?.errors || []).slice(0, 20).map((e, i) => (
                            <div key={`err-${e.matricola}-${i}`}>Matr. {e.matricola}: {e.error}</div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    onClick={reset}
                    className={result ? "" : "hidden"}
                    data-testid="migration-libro-reset"
                >
                    Nuovo file
                </Button>
                <Button
                    variant="outline"
                    onClick={downloadPdf}
                    className={`gap-2 ${result ? "" : "hidden"}`}
                    data-testid="migration-libro-report-pdf"
                >
                    <FileDown className="h-4 w-4" />
                    Scarica report PDF
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!file || busy}
                    className={`gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90 ${result ? "hidden" : ""}`}
                    data-testid="migration-libro-submit"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Aggiorna anagrafica
                </Button>
            </div>
        </div>
    );
};


// ---------------------------------------------------------------------------
// USERS panel — upload users.json (kept as fallback)
// ---------------------------------------------------------------------------
const UsersImportPanel = ({ onImported }) => {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const ref = useRef(null);

    const reset = () => { setFile(null); setResult(null); if (ref.current) ref.current.value = ""; };

    const handleSubmit = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const text = await file.text();
            let parsed;
            try {
                parsed = JSON.parse(text);
            } catch (e) {
                toast.error("File JSON non valido");
                setImporting(false);
                return;
            }
            const users = parsed.users || parsed;
            if (!Array.isArray(users)) {
                toast.error("Atteso oggetto {users: [...]} oppure un array");
                setImporting(false);
                return;
            }
            const { data } = await api.post("/migration/users", { users });
            setResult(data);
            toast.success(`${data.created} creati, ${data.updated} aggiornati`);
            onImported && onImported();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore import utenti");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
                Metodo alternativo: carica un file <code>users.json</code> nel formato <code>{`{users: [...]}`}</code>.
            </div>
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
                <FileJson className="mx-auto h-8 w-8 text-slate-400" />
                <div className="mt-2 font-display text-sm font-semibold text-[#376DB3]">
                    {file ? file.name : "users.json"}
                </div>
                {file && <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>}
                <input
                    ref={ref}
                    type="file"
                    accept=".json,application/json"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                    className="hidden"
                    data-testid="migration-users-input"
                />
                <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => ref.current?.click()}>
                    <Upload className="h-4 w-4" />
                    {file ? "Cambia file" : "Seleziona users.json"}
                </Button>
            </div>

            <div className={`space-y-2 ${result ? "" : "hidden"}`}>
                <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <div className="text-sm text-emerald-900">
                        <b>{result?.created || 0}</b> creati, <b>{result?.updated || 0}</b> aggiornati, <b>{result?.password_imported || 0}</b> con password importata
                        <span className={result?.needs_reset > 0 ? "" : "hidden"}>, <b className="text-amber-700">{result?.needs_reset || 0}</b> necessitano reset</span>
                    </div>
                </div>
                <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 ${result?.errors?.length > 0 ? "" : "hidden"}`}>
                    <div className="font-semibold">Errori ({result?.errors?.length || 0})</div>
                    <div className="mt-1 max-h-32 overflow-y-auto">
                        {(result?.errors || []).slice(0, 20).map((e, i) => (
                            <div key={`err-${e.index}-${i}`}>Riga {e.index}: {e.error}</div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={reset}
                    className={result ? "" : "hidden"}
                >
                    Nuovo import
                </Button>
                <Button
                    variant="outline"
                    onClick={() => buildMigrationReportPdf(result)}
                    className={`gap-2 ${result ? "" : "hidden"}`}
                    data-testid="migration-users-report-pdf"
                >
                    <FileDown className="h-4 w-4" />
                    Scarica report PDF
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!file || importing}
                    className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                    data-testid="migration-users-submit"
                >
                    {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Importa utenti
                </Button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// FILES panel — upload chunked zips
// ---------------------------------------------------------------------------
const FilesImportPanel = ({ onImported }) => {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const ref = useRef(null);

    const reset = () => { setFile(null); setResult(null); if (ref.current) ref.current.value = ""; };

    const handleSubmit = async () => {
        if (!file) return;
        setImporting(true);
        const fd = new FormData();
        fd.append("bundle", file);
        try {
            const { data } = await api.post("/migration/files", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000,
            });
            setResult(data);
            const dupMsg = data.already_present > 0 ? `, ${data.already_present} già presenti (saltati)` : "";
            toast.success(`${data.created} documenti importati${dupMsg}`);
            onImported && onImported();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore import documenti");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <b>Importa prima gli utenti</b>, poi carica i file ZIP uno alla volta (max 600 MB per chunk).
            </div>
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
                <Files className="mx-auto h-8 w-8 text-slate-400" />
                <div className="mt-2 font-display text-sm font-semibold text-[#376DB3]">
                    {file ? file.name : "documenti.zip"}
                </div>
                {file && <div className="text-xs text-slate-500">{formatBytes(file.size)}</div>}
                <input
                    ref={ref}
                    type="file"
                    accept=".zip"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                    className="hidden"
                    data-testid="migration-files-input"
                />
                <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => ref.current?.click()}>
                    <Upload className="h-4 w-4" />
                    {file ? "Cambia file" : "Seleziona ZIP"}
                </Button>
            </div>

            <div className={`space-y-2 ${result ? "" : "hidden"}`}>
                <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <div className="text-sm text-emerald-900" data-testid="migration-files-result">
                        <b>{result?.created || 0}</b> documenti importati, <b>{result?.already_present || 0}</b> già presenti (saltati), <b>{result?.skipped || 0}</b> con errori
                    </div>
                </div>
                <div className={`rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 ${result?.errors?.length > 0 ? "" : "hidden"}`}>
                    <div className="flex items-center gap-1 font-semibold">
                        <AlertTriangle className="h-3 w-3" /> Errori ({result?.errors?.length || 0})
                    </div>
                    <div className="mt-1 max-h-40 overflow-y-auto">
                        {(result?.errors || []).slice(0, 30).map((e, i) => (
                            <div key={`err-${e.filename}-${i}`}><b>{e.filename}:</b> {e.error}</div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={reset}
                    className={result ? "" : "hidden"}
                >
                    Carica prossimo ZIP
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!file || importing}
                    className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                    data-testid="migration-files-submit"
                >
                    {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Importa documenti
                </Button>
            </div>
        </div>
    );
};
